import React, { useRef, useState, useEffect } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm         from '../../components/RSVPForm';
import Wishlist         from '../../components/Wishlist';
import YandexMap        from '../../components/YandexMap';
import Countdown        from '../../components/Countdown';
import TickerBar        from '../../components/TickerBar';
import FixedCTA         from '../../components/FixedCTA';
import useReveal        from '../../components/useReveal';
import Schedule         from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import CalendarButton   from '../../components/CalendarButton';
import BlockSubtitle    from '../../components/BlockSubtitle';
import PaidWatermark from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './Amorika.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : (u.startsWith('http') ? u : u);
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

const fmtDateDots = raw => {
  if (!raw) return '??.??.????';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join('.');
};

const MON_RU_NOM = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

function buildMonthGrid(raw) {
  if (!raw) return { title: 'Апрель', cells: [], activeDay: null };
  const d = new Date(raw);
  if (isNaN(d.getTime())) return { title: 'Апрель', cells: [], activeDay: null };
  const year  = d.getFullYear();
  const month = d.getMonth();
  const active = d.getDate();

  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = (first.getDay() + 6) % 7; // Mon=0..Sun=6

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let i = 1; i <= daysInMonth; i++) cells.push(i);
  while (cells.length % 7 !== 0) cells.push(null);
  return { title: MON_RU_NOM[month], cells, activeDay: active };
}

/* Load Caveat + Montserrat once (also loaded via index.html as a fallback). */
function useFonts() {
  useEffect(() => {
    const id = 'amo-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Montserrat:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);
}

/* ── Class maps for shared components ─────────────────────── */
const AMO_TICKER_CLASSES = { bar: 'amo-ticker', track: 'amo-ticker-track', item: 'amo-ticker-item', sep: 'amo-ticker-sep' };
const AMO_CD_CLASSES     = { root: 'amo-cd',     cell: 'amo-cd-cell', num: 'amo-cd-num', label: 'amo-cd-lbl' };
const AMO_SCHED_CLASSES  = { root: 'amo-sched',  row: 'amo-sched-row', rowBorder: 'amo-sched-row--border', time: 'amo-sched-time', info: 'amo-sched-info', name: 'amo-sched-name', desc: 'amo-sched-desc' };
const AMO_DRESS_CLASSES  = { wrap: 'amo-swatches', swatchWrap: 'amo-swatch-wrap', swatch: 'amo-swatch', swatchLabel: 'amo-swatch-lbl', photos: 'amo-dc-photos', photo: 'amo-dc-photo' };
const AMO_RSVP_CLASSES = {
  form:    'amo-form',
  tabs:    'amo-rsvp-tabs',
  tab:     'amo-rsvp-tab',
  tabSel:  'amo-rsvp-tab--sel',
  field:   'amo-form-field',
  label:   'amo-form-lbl',
  input:   'amo-form-input',
  submit:  'amo-btn amo-btn--primary amo-btn--pill amo-btn--full',
  success: 'amo-form-ok',
  error:   'amo-form-err',
  chip:    'amo-chip',
  chipSel: 'amo-chip--sel',
  chips:   'amo-form-chips',
};
const AMO_WISHLIST_CLASSES = {
  list:        'amo-wish',
  item:        'amo-wish-item',
  itemBorder:  'amo-wish-item--border',
  row:         'amo-wish-row',
  name:        'amo-wish-name',
  price:       'amo-wish-price',
  btn:         'amo-btn amo-btn--outline amo-btn--pill amo-btn--full',
  btnReserved: 'amo-btn--res',
};
const AMO_WISHLIST_MODAL_CLASSES = {
  bg:         'amo-wish-modal-bg',
  modal:      'amo-wish-modal',
  title:      'amo-wish-modal-title',
  subtitle:   'amo-wish-modal-sub',
  input:      'amo-form-input',
  error:      'amo-form-err',
  btns:       'amo-wish-modal-btns',
  btnCancel:  'amo-btn amo-btn--outline amo-btn--pill',
  btnConfirm: 'amo-btn amo-btn--primary amo-btn--pill',
};

const ASSET = '/templates/amorika';
/* Перекраска декора в палитру включена для проектов, созданных с этой даты
   (полночь по МСК 6 июля 2026). Более старые проекты рендерятся как раньше
   (декор в исходном красном). */
const RECOLOR_SINCE = Date.parse('2026-07-06T00:00:00+03:00');

/* Инлайн декоративного SVG с перекраской в палитру шаблона.
   Декор-svg собраны из Figma с дефолтными цветами Amorika
   (#BE0026 accent, #FFEEF4 bg, #3A2E32 fg). Через <img> CSS-переменные
   не доходят, поэтому подменяем hex прямо в разметке и инлайним. */
const _amoSvgCache = {};
function AmoDeco({ file, className, colors }) {
  const src = `${ASSET}/${file}`;
  const [raw, setRaw] = useState(() => (_amoSvgCache[src] != null ? _amoSvgCache[src] : null));
  useEffect(() => {
    if (_amoSvgCache[src] != null) { setRaw(_amoSvgCache[src]); return; }
    let alive = true;
    fetch(src).then(r => (r.ok ? r.text() : '')).then(t => { _amoSvgCache[src] = t; if (alive) setRaw(t); }).catch(() => {});
    return () => { alive = false; };
  }, [src]);
  const cls = `amo-svg${className ? ' ' + className : ''}`;
  if (!raw) return <span className={cls} aria-hidden="true" />;
  let html = raw;
  (colors || []).forEach(([from, to]) => { if (to && from !== to) html = html.split(from).join(to); });
  return <span className={cls} aria-hidden="true" dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ══ Main Template — Amorika ════════════════════════════════════ */
export default function Amorika({ project, design, isPreview }) {
  const pageRef  = useRef(null);
  const blockDep = project?.blocks?.length;
  useReveal(pageRef, blockDep, { revealSelector: '.amo-reveal', inClass: 'amo-in' });
  useFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};

  const accent = d.palette?.primary   || p.palette?.primary   || '#BE0026';
  const bg     = d.palette?.secondary || p.palette?.secondary || '#FFEEF4';
  const fg     = d.palette?.textDark  || p.palette?.textDark  || '#3A2E32';
  /* Перекраска декора в палитру — только для проектов, созданных начиная с
     RECOLOR_SINCE. Старые проекты сохраняют прежний вид (красный декор). */
  const createdTs = project?.createdAt ? new Date(project.createdAt).getTime() : Date.now();
  const recolorDeco = createdTs >= RECOLOR_SINCE;
  const svgColors = recolorDeco ? [['#BE0026', accent], ['#FFEEF4', bg], ['#3A2E32', fg]] : [];
  const decoAccent = recolorDeco ? accent : '#BE0026';

  const blocks = p.blocks || ['menu','map','schedule','palette','rsvp','survey','message','wishlist','countdown','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');

  const bride       = p.bride || '';
  const groom       = p.groom || '';
  const tagline     = p.block_content?.header?.subtitle || 'Не прошло и 5-ти лет';
  const footerQuote = p.block_content?.footer?.quote    || '';
  const invText     = p.invitation_text || p.block_content?.message?.text || '';
  const invTitle    = p.block_content?.message?.title || 'Дорогие наши!';

  const photos       = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto1   = photos[0] || null;
  const heroPhoto2   = photos[1] || null;
  const middlePhoto  = photos[2] || photos[0] || null;
  const dcPhotos     = p.dresscode_photos || [];
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;

  const dateStr = fmtDateDots(p.wedding_date);
  const monthGrid = buildMonthGrid(p.wedding_date);

  const schedule = p.schedule?.length ? p.schedule : [];
  const venues   = p.venues || [];
  const swatches = p.dresscode_palette || [];
  const wishlist = p.wishlist || [];

  const bc = p.block_content || {};
  const titleSchedule = bc.schedule?.title  || 'Программа';
  const titleLocation = bc.map?.title       || 'Локация';
  const titleDress    = bc.palette?.title   || 'Дресс-код';
  const titleRsvp     = bc.rsvp?.title      || 'Подтвердите присутствие';
  const titleWish     = bc.wishlist?.title  || 'Что дарить';
  const titleCD       = bc.countdown?.title || 'До нашей свадьбы';
  const calBtn        = bc.calendar?.title  || 'Добавить в календарь';

  return (
    <div className={`amo-page${inPreview ? ' amo-page--preview' : ''}`} ref={pageRef}
      style={{ '--amo-accent': accent, '--amo-bg': bg, '--amo-fg': fg, '--amo-deco-accent': decoAccent }}>

      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* Ticker */}
      <TickerBar text={dateStr} classes={AMO_TICKER_CLASSES} />

      {/* Navigation */}
      {HAS_MENU && (
        <nav className="amo-nav">
          {blocks.includes('schedule') && <a href="#amo-schedule">{bc.menu?.schedule || 'Программа'}</a>}
          {blocks.includes('map')      && <a href="#amo-location">{bc.menu?.location || 'Локация'}</a>}
          {blocks.includes('palette')  && <a href="#amo-dresscode">{bc.menu?.dresscode || 'Дресс-код'}</a>}
          {blocks.includes('rsvp')     && <a href="#amo-rsvp">{bc.menu?.rsvp || 'Анкета'}</a>}
        </nav>
      )}

      {/* Hero — name card + two polaroid photos + decorative hearts */}
      <section className="amo-hero amo-reveal" data-nophoto={(heroPhoto1?.url || heroPhoto2?.url) ? undefined : '1'}>
        <AmoDeco file="heart-bow.svg" className="amo-hero-bow" colors={svgColors} />
        <div className="amo-hero-card">
          {tagline && <p className="amo-hero-tagline">{tagline}</p>}
          <div className="amo-hero-names">
            {bride && <span className="amo-hero-name">{bride}</span>}
            <span className="amo-hero-amp">{bc.header?.amp || 'и'}</span>
            {groom && <span className="amo-hero-name">{groom}</span>}
          </div>
        </div>
        {(heroPhoto1?.url || heroPhoto2?.url) && (
          <div className="amo-hero-photos">
            {heroPhoto1?.url && (
              <div className="amo-polaroid amo-polaroid--left">
                <div className="amo-polaroid-inner">
                  <img src={src(heroPhoto1.url)} alt="" style={{ filter: flt(heroPhoto1), objectPosition: objPos(heroPhoto1) }} />
                </div>
              </div>
            )}
            {heroPhoto2?.url && (
              <div className="amo-polaroid amo-polaroid--right">
                <div className="amo-polaroid-inner">
                  <img src={src(heroPhoto2.url)} alt="" style={{ filter: flt(heroPhoto2), objectPosition: objPos(heroPhoto2) }} />
                </div>
              </div>
            )}
          </div>
        )}
        <AmoDeco file="heart-arrow.svg" className="amo-hero-arrow" colors={svgColors} />
        <AmoDeco file="heart-big.svg" className="amo-hero-heart" colors={svgColors} />
      </section>

      {/* Invitation letter */}
      <section className="amo-pad amo-invite amo-reveal">
        <h2 className="amo-h-script amo-h-script--center">{invTitle}</h2>
        <div className="amo-invite-body">
          {invText
            ? invText.split('\n').map((line, i) =>
                line.trim() ? <p key={i}>{line}</p> : <br key={i} />
              )
            : null
          }
        </div>
      </section>

      {/* Calendar */}
      {HAS_CALENDAR && (
        <section className="amo-pad amo-cal-sect amo-reveal">
          <div className="amo-cal-inner">
            <h3 className="amo-cal-title">{monthGrid.title}</h3>
            <div className="amo-cal-grid">
              {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(dd => (
                <span key={dd} className="amo-cal-dow">{dd}</span>
              ))}
              {monthGrid.cells.map((c, i) => (
                <span key={i}
                  className={`amo-cal-day${c === monthGrid.activeDay ? ' amo-cal-day--on' : ''}${c === null ? ' amo-cal-day--empty' : ''}`}>
                  {c != null ? <span className="amo-cal-day-inner">{c}</span> : ''}
                </span>
              ))}
            </div>
            <CalendarButton
              bride={bride}
              groom={groom}
              date={p.wedding_date}
              time={p.wedding_time}
              venues={p.venues}
              address={p.wedding_address}
              className="amo-btn amo-btn--outline amo-btn--pill amo-btn--full">
              {calBtn}
            </CalendarButton>
          </div>
        </section>
      )}

      {/* Dynamic blocks — Figma order: map → schedule → (middle-photo) → palette → rsvp → wishlist → countdown */}
      {['map','schedule','midphoto','palette','rsvp','wishlist','countdown']
        .filter(b => b === 'midphoto' ? true : blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              return (
                <section key="map" className="amo-map-sect amo-reveal" id="amo-location">
                  <div className="amo-pad amo-map-head">
                    <h2 className="amo-h-script amo-h-script--center">{titleLocation}</h2>
                    <BlockSubtitle text={bc.map?.subtitle} className="amo-p amo-p--center" />
                    {venues.map((v, i) => (
                      <div key={i} className="amo-addr">
                        {(v.address || '').trim() && <AmoDeco file="location-pin.svg" className="amo-addr-icon" colors={svgColors} />}
                        <span className="amo-addr-text">
                          {v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}
                        </span>
                      </div>
                    ))}
                    {!venues.some(v => v.address) && p.wedding_address && (
                      <div className="amo-addr">
                        <AmoDeco file="location-pin.svg" className="amo-addr-icon" colors={svgColors} />
                        <span className="amo-addr-text">{p.wedding_address}</span>
                      </div>
                    )}
                  </div>
                  <div className="amo-pad">
                    {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="amo-map-wrap">
                      {(() => {
                        if (p.map_url) {
                          return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                        }
                        const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                        if (withCoords.length > 0) {
                          return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#redIcon" />;
                        }
                        const addr = venues.length > 0
                          ? venues.map(v => v.address).filter(Boolean).join(' — ')
                          : (p.wedding_address || '');
                        if (!addr) return null;
                        const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                        return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                      })()}
                    </div>
                  )}
                  </div>
                </section>
              );

            case 'schedule':
              return (
                <section key="schedule" className="amo-pad amo-sched-sect amo-reveal" id="amo-schedule">
                  <h2 className="amo-h-script amo-h-script--center">{titleSchedule}</h2>
                  <BlockSubtitle text={bc.schedule?.subtitle} className="amo-p amo-p--center" />
                  <div className="amo-sched-wrap">
                    <div className="amo-sched-line" aria-hidden="true" />
                    <Schedule items={schedule} classes={AMO_SCHED_CLASSES} />
                  </div>
                </section>
              );

            case 'midphoto':
              return (
                <section key="midphoto" className="amo-mid-photo amo-reveal">
                  <div className="amo-mid-photo-inner">
                    <div className="amo-mid__grid-bg"><AmoDeco file="mid-grid-bg.svg" colors={svgColors} /></div>
                    <div className="amo-mid__grid-front"><AmoDeco file="mid-grid-front.svg" colors={svgColors} /></div>
                    <div className="amo-mid__sticky"><AmoDeco file="mid-sticky.svg" colors={svgColors} /></div>
                    <div className="amo-mid__tape"><AmoDeco file="mid-tape.svg" colors={svgColors} /></div>
                    <div className="amo-mid__hearts-left"><AmoDeco file="mid-hearts-left.svg" colors={svgColors} /></div>
                    <div className="amo-mid__hearts-right"><AmoDeco file="mid-hearts-right.svg" colors={svgColors} /></div>
                    {middlePhoto?.url && (
                    <div className="amo-mid__polaroid">
                      <div className="amo-mid__polaroid-inner">
                        <img src={src(middlePhoto.url)} alt="" style={{ filter: flt(middlePhoto), objectPosition: objPos(middlePhoto) }} />
                      </div>
                    </div>
                    )}
                  </div>
                </section>
              );

            case 'palette': {
              const heartColors = swatches.slice(0, 6).map(s => s?.color || s);
              return (
                <section key="palette" className="amo-pad amo-dress-sect amo-reveal" id="amo-dresscode">
                  <h2 className="amo-h-script amo-h-script--center">{titleDress}</h2>
                  <BlockSubtitle text={bc.palette?.subtitle} className="amo-p amo-p--center" />
                  {heartColors.length > 0 && (
                    /* Десктоп — все сердечки в один ряд; мобайл — по 3 в ряд
                       (раскладка управляется в CSS, см. .amo-dress-hearts). */
                    <div className="amo-dress-hearts" aria-hidden="true">
                      {heartColors.map((col, i) => (
                        <svg key={i} className="amo-dress-heart" viewBox="0 0 54.2 54.2" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.52 19.2C4.52 12.34 10.08 6.78 16.94 6.78C21.14 6.78 24.85 8.86 27.1 12.05C29.35 8.86 33.06 6.78 37.26 6.78C44.12 6.78 49.69 12.34 49.69 19.2C49.69 36.13 27.1 48.52 27.1 48.52C27.1 48.52 4.52 36.13 4.52 19.2Z"
                            fill={col}
                            stroke={/^#f|^#e|white/i.test(String(col)) ? 'rgba(58,46,50,0.18)' : 'none'}
                            strokeWidth="1" />
                        </svg>
                      ))}
                    </div>
                  )}
                  {dcPhotos.length > 0 && (
                    <DresscodePalette
                      swatches={[]}
                      photos={dcPhotos}
                      cssFilter={flt}
                      classes={AMO_DRESS_CLASSES}
                    />
                  )}
                </section>
              );
            }

            case 'rsvp':
              return (
                <section key="rsvp" className="amo-pad amo-rsvp-sect amo-reveal" id="amo-rsvp">
                  <h2 className="amo-h-script amo-h-script--center">{titleRsvp}</h2>
                  <BlockSubtitle text={bc.rsvp?.subtitle} className="amo-p amo-p--center" />
                  <RSVPForm project={p} blocks={blocks} classes={AMO_RSVP_CLASSES} />
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="amo-pad amo-wish-sect amo-reveal">
                  <h2 className="amo-h-script amo-h-script--center">{titleWish}</h2>
                  <BlockSubtitle text={bc.wishlist?.subtitle} className="amo-p amo-p--center" />
                  <Wishlist
                    items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                    slug={p.slug}
                    classes={AMO_WISHLIST_CLASSES}
                    modalClasses={AMO_WISHLIST_MODAL_CLASSES}
                  />
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="amo-pad amo-cd-sect amo-reveal">
                  <h2 className="amo-h-script amo-h-script--center">{titleCD}</h2>
                  <BlockSubtitle text={bc.countdown?.subtitle} className="amo-p amo-p--center" />
                  <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={AMO_CD_CLASSES} />
                </section>
              );

            default:
              return null;
          }
        })}

      {/* Footer — photo + red quote panel with heart decoration above */}
      <footer className="amo-footer amo-reveal">
        {footerPhotoObj?.url && (
          <div className="amo-footer-photo">
            <img src={src(footerPhotoObj.url)} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        )}
        <div className="amo-footer-quote">
          <p>{(footerQuote || 'Одна любовь, другой такой и не было').split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}</p>
        </div>
      </footer>

      {/* Fixed RSVP CTA */}
      {blocks.includes('rsvp') && (
        <FixedCTA
          href="#amo-rsvp"
          targetId="amo-rsvp"
          scrollRoot={pageRef}
          className="amo-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
